import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../constants/svgIcon';
import { InAppEvaluations } from '../../../models/GameControllerStore';
import { GameType } from '../../../models/GameStore';
import { Profile } from '../../../models/UserStore';
import { AvatarNode, ProgressBar, SvgIcon } from '../../Molecules';

import './GameHud.css';

export type GameHudProps = {
  profiles: Profile[];
  gameType: GameType | undefined;
  scores: number[];
  elapsedTime: string;
  instruction?: string;
  deductingScore?: number;
  challengeTitle?: string;
  challengeGameIndex?: number;
  challengeGameCount?: number;
  evaluation?: InAppEvaluations | null | undefined;
  withAIBadge?: boolean;
  onClickExpandInstruction?: () => void;
};

export const GameHud = memo(
  ({
    profiles,
    gameType,
    scores,
    elapsedTime,
    instruction,
    deductingScore,
    challengeTitle,
    challengeGameIndex,
    challengeGameCount,
    evaluation,
    // onClickExpandInstruction,
    withAIBadge,
  }: GameHudProps) => {
    const profilesLength = profiles.length;
    const scoresLength = scores.length;
    const total = scores.reduce((acc, cur) => (acc += cur), 0);

    const isTutorial = profilesLength === scoresLength && profilesLength === 1;

    return (
      <div className="game-hud-container">
        <div
          className={`game-hud ${
            gameType === GameType.FingerWriting ? 'orangeHudBar' : 'blueHudBar'
          }`}
        >
          {profilesLength === scoresLength && profilesLength === 1 ? (
            challengeTitle ? (
              <ChallengeHud
                profiles={profiles}
                gameType={gameType}
                scores={scores}
                elapsedTime={elapsedTime}
                instruction={instruction}
                deductingScore={deductingScore}
                challengeTitle={challengeTitle}
                challengeGameIndex={challengeGameIndex}
                challengeGameCount={challengeGameCount}
              />
            ) : (
              <TutorialHud
                profiles={profiles}
                gameType={gameType}
                scores={scores}
                elapsedTime={elapsedTime}
                instruction={instruction}
                deductingScore={deductingScore}
                evaluation={evaluation}
              />
            )
          ) : null}
          {profilesLength === scoresLength && profilesLength === 2 ? (
            <MemoryGameHud
              profiles={profiles}
              gameType={gameType}
              scores={scores}
              elapsedTime={elapsedTime}
              instruction={instruction}
              deductingScore={deductingScore}
              withAIBadge={withAIBadge}
            />
          ) : null}
          <ScoresBar scores={scores} total={total} isTutorial={isTutorial} />
        </div>
        {/* {instruction && (
          <div className="question-container">
            {questionAudioSrc ? (
              <TTSPlayer
                audioSrc={questionAudioSrc}
                onClickPlay={onClickAudio}
              />
            ) : null}

            <p className="question-text">{instruction}</p>

            {onClickExpandInstruction && (
              <div
                className="expand-question-button"
                onClick={onClickExpandInstruction}
              >
                <SvgIcon
                  name={SVGIconType.Arrow}
                  width={20}
                  height={20}
                  color="#808080"
                />
              </div>
            )}
          </div>
        )} */}
      </div>
    );
  },
);

const Timer = memo(({ elapsedTime }: { elapsedTime: string }) => {
  return (
    <div className="timer">
      {/* <img className="icon" src={Icons[IconType.TimeLeft]} /> */}
      <SvgIcon name={SVGIconType.Timer} width={20} height={20} />
      <p className="text">{elapsedTime}</p>
    </div>
  );
});

const TutorialHud = memo(
  ({
    profiles,
    scores,
    elapsedTime,
    deductingScore,
    evaluation,
  }: GameHudProps) => {
    const { t } = useTranslation();
    // Ensure scores[0] has a default value to avoid undefined errors
    const playerScore = scores[0] ?? 0;

    // Determine the animation class based on the score
    const scoreAnimationClass =
      playerScore <= 20 ? 'blink-low' : playerScore <= 49 ? 'blink-mid' : '';
    return (
      <div className="tutorial-hud">
        <div
          className="avatar-container"
          style={{
            paddingLeft: 96 / 1.5,
          }}
        >
          {profiles[0] && <AvatarNode profile={profiles[0]} size={44} />}

          <div
            className={`${'score-tag-container'}`}
            style={{
              marginRight: 96,
            }}
          >
            <div
              className={`${'current-player-score-tag'} ${scoreAnimationClass}`}
              style={{
                backgroundColor: '#78B810',
              }}
            >
              {playerScore}
            </div>

            <div
              style={{
                marginTop: '5.5rem',
                position: 'absolute',
                zIndex: 2,
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
              }}
            >
              {deductingScore && deductingScore !== 0 ? (
                <DeductScore deductingScore={deductingScore} />
              ) : null}
            </div>
          </div>
        </div>

        <div className="tutorial-info">
          <p className="tutorial-info-mode-text">
            {evaluation === undefined
              ? t('Interactive Learning')
              : evaluation === null
              ? t('Full Practice')
              : t('Improvement Practice')}
          </p>
          {/* <p className="tutorial-info-game-type-text">
            {t(getGameTypeDetails(gameType).displayName)}
          </p> */}
          <div style={{ display: 'block' }}>
            <Timer elapsedTime={elapsedTime} />
          </div>
        </div>
      </div>
    );
  },
);

const MemoryGameHud = memo(
  ({
    profiles,
    scores,
    elapsedTime,
    deductingScore,
    withAIBadge,
  }: GameHudProps) => {
    const player1Score = scores[0] ?? 0;

    // Determine the animation class based on the score
    const scoreAnimationClass =
      player1Score <= 20 ? 'blink-low' : player1Score <= 49 ? 'blink-mid' : '';

    return (
      <div className="inner-hud">
        <div className="avatar-group">
          <div
            className="avatar-container"
            style={{
              paddingLeft: 96 / 1.5,
            }}
          >
            {profiles[0] && <AvatarNode profile={profiles[0]} size={44} />}

            <div
              className={`${'score-tag-container'}`}
              style={{
                marginRight: 96,
              }}
            >
              <div
                className={`${'current-player-score-tag'} ${scoreAnimationClass}`}
                style={{
                  backgroundColor: '#78B810',
                }}
              >
                {scores[0]}
              </div>

              <div
                style={{
                  marginTop: '5.5rem',
                  position: 'absolute',
                  zIndex: 2,
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                {deductingScore && deductingScore !== 0 ? (
                  <DeductScore deductingScore={deductingScore} />
                ) : null}
              </div>
            </div>
          </div>

          <div
            className="avatar-container"
            style={{
              paddingRight: 96 / 1.5,
            }}
          >
            <div className="opponent-avatar">
              {profiles[1] && <AvatarNode profile={profiles[1]} size={44} />}
              {withAIBadge && (
                <div className="ai-badge">
                  <span className="ai-badge-label">AI</span>
                </div>
              )}
            </div>
            <div
              className="score-tag"
              style={{
                marginLeft: 96,
                backgroundColor: FactionColors.Secondary,
              }}
            >
              {scores[1]}
            </div>
          </div>
        </div>
        <div className="memory-game-hub-timer">
          <Timer elapsedTime={elapsedTime} />
        </div>
        <div className="vs-flag">
          <p className="vs-flag-label">VS</p>
        </div>
      </div>
    );
  },
);

const ChallengeHud = memo(
  ({
    profiles,
    scores,
    elapsedTime,
    deductingScore,
    challengeTitle,
    challengeGameIndex,
    challengeGameCount,
  }: GameHudProps) => {
    const { t } = useTranslation();
    // Ensure scores[0] has a default value to avoid undefined errors
    const playerScore = scores[0] ?? 0;

    // Determine the animation class based on the score
    const scoreAnimationClass =
      playerScore <= 20 ? 'blink-low' : playerScore <= 49 ? 'blink-mid' : '';
    return (
      <div className="tutorial-hud">
        <div
          className="avatar-container"
          style={{
            paddingLeft: 96 / 1.5,
          }}
        >
          {profiles[0] && <AvatarNode profile={profiles[0]} size={44} />}

          <div
            className={`${'score-tag-container'}`}
            style={{
              marginRight: 96,
            }}
          >
            <div
              className={`${'current-player-score-tag'} ${scoreAnimationClass}`}
              style={{
                backgroundColor: '#78B810',
              }}
            >
              {playerScore}
            </div>

            <div
              style={{
                marginTop: '5.5rem',
                position: 'absolute',
                zIndex: 2,
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
              }}
            >
              {deductingScore && deductingScore !== 0 ? (
                <DeductScore deductingScore={deductingScore} />
              ) : null}
            </div>
          </div>
        </div>
        <div className="tutorial-info">
          <div className="tutorial-info-mode-text-container">
            <SvgIcon
              name={
                // TODO: may need to use enum?
                challengeTitle === 'Super Challenge'
                  ? SVGIconType.SuperTarget
                  : SVGIconType.MiniTarget
              }
              width={20}
              height={20}
              color="white"
            />
            <p className="tutorial-info-mode-text">{t(challengeTitle ?? '')}</p>
          </div>
          <ProgressBar
            current={(challengeGameIndex ?? 0) + 1}
            total={challengeGameCount ?? 0}
            width={'150px'}
            textColor="#fff"
          />
          <div style={{ display: 'block' }}>
            <Timer elapsedTime={elapsedTime} />
          </div>
        </div>
      </div>
    );
  },
);

enum FactionColors {
  Primary = '#85CC12',
  Secondary = '#BF54C9',
  Warning = '#f9a623',
  Danger = '#eb4d4d',
}

type ScoresBarProps = {
  scores: number[];
  total: number;
  isTutorial?: boolean;
};

const ScoresBar: React.FC<ScoresBarProps> = memo(
  ({ scores, total, isTutorial = false }) => {
    const tutorialColor = FactionColors.Primary;

    return (
      <div className="scores-bar">
        {isTutorial ? (
          <div
            className="score-bar"
            style={{
              backgroundColor: tutorialColor,
              flex: 1,
            }}
          />
        ) : (
          scores.map((score, index) => {
            const color = index === 1 ? FactionColors.Secondary : '#85CC12';
            // : score <= 20
            // ? FactionColors.Danger
            // : score < 50
            // ? FactionColors.Warning
            // : FactionColors.Primary;

            return (
              <ScoreBar key={index} score={score} total={total} color={color} />
            );
          })
        )}

        {scores.length > 1 && <div className="scores-bar-center-indicator" />}
      </div>
    );
  },
);

const DeductScore = memo(({ deductingScore }: { deductingScore: number }) => {
  return (
    <div className="deduct-score">
      <span className={deductingScore > 0 ? 'score-text-white' : 'score-text'}>
        {deductingScore > 0 ? '+' : ''}
        {deductingScore}
      </span>
    </div>
  );
});

const getScoreColor = (
  score: number,
  total: number,
  mainColor: string = '#85cc12',
) => {
  const color =
    score >= 50
      ? mainColor
      : score > 20
      ? FactionColors.Warning
      : FactionColors.Danger;
  const flex = (score / (total == 0 ? 1 : total)) * 100;
  return { color, flex };
};

const ScoreBar = memo(
  ({
    score,
    total,
    color,
  }: {
    score: number;
    total: number;
    color: string;
  }) => {
    const { flex } = getScoreColor(score, total);
    return (
      <div
        className="score-bar"
        style={{
          backgroundColor: color,
          flex: flex,
        }}
      />
    );
  },
);
